import React, { useEffect, useState } from "react";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { ChoiceGroup, Dropdown, IChoiceGroupOption, IconButton, ResponsiveMode, TeachingBubble } from "@fluentui/react";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { TextField } from "@fluentui/react/lib/TextField";
import { FormatPreview } from "../../../api/models/format-preview";
import { FormatTypeEnum } from "../../../api/models/format-type-enum";
import { formatPreview } from "../../../api/document/Format";
import { Label } from "@fluentui/react/lib/Label";
import { Toggle } from "@fluentui/react/lib/Toggle";
import {
  AdvancedDocumentMetadata,
  DocumentMetadata,
  getDocumentAdvancedCustomXml,
} from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { DocumentPart } from "../../../api/models";
import {
  getDefaultBuiltInFormattingString,
  getDefaultCustomFormattingString,
} from "../../../utils/helpers/ObjectFormattingHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { fetchDocumentPartDetails } from "../../../api/document/DocumentsPart";
import { builtInNumberFormat, builtInPercentageFormat } from "../../../utils/constants/formats";
import { InfoState } from "../../document/AdvancedDocumentPanel";

interface CoObjectAdvancedAmountProps {
  objectAdvanced: AdvancedDocumentMetadata;
  updateCoObjectAdvancedAmount: (obj: AdvancedDocumentMetadata, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  documentPartId: string;
}

const DEFAULT_VALUE = "1234567.89";
const TYPE_DEFAULT = "Default";
const TYPE_CUSTOM = "Custom";

const getUpdatedAdvancedDocument: (
  objectAdvanced: AdvancedDocumentMetadata,
  documentPart: DocumentPart,
  editAmountCustomXML: DocumentMetadata
) => AdvancedDocumentMetadata | null = (objectAdvanced, documentPart, editAmountCustomXML) => {
  if (!objectAdvanced?.inheritFormat) {
    return {
      numberFormat: objectAdvanced?.numberFormat ?? getDefaultCustomFormattingString(FormatTypeEnum.NUMBER),
      typeNumber: objectAdvanced.typeNumber ?? TYPE_CUSTOM,
      percentageFormat: objectAdvanced?.percentageFormat ?? getDefaultCustomFormattingString(FormatTypeEnum.PERCENTAGE),
      typePercentage: objectAdvanced.typePercentage ?? TYPE_CUSTOM,
      dateFormat: objectAdvanced.dateFormat,
      typeDate: objectAdvanced.typeDate,
      locale: editAmountCustomXML?.advanced.locale,
      id: objectAdvanced.id,
      choice: objectAdvanced.choice,
      inheritFormat: false,
    };
  }
  //I'm inheriting
  if (documentPart?.root) {
    return {
      numberFormat: editAmountCustomXML.advanced.numberFormat,
      typeNumber: editAmountCustomXML.advanced.typeNumber ?? TYPE_CUSTOM,
      percentageFormat: editAmountCustomXML.advanced.percentageFormat,
      typePercentage: editAmountCustomXML.advanced.typePercentage ?? TYPE_CUSTOM,
      dateFormat: editAmountCustomXML.advanced.dateFormat,
      typeDate: editAmountCustomXML.advanced.typeDate ?? TYPE_CUSTOM,
      locale: editAmountCustomXML.advanced.locale,
      id: editAmountCustomXML.advanced.id,
      choice: objectAdvanced.choice,
      inheritFormat: true,
    };
  }
  if (!documentPart?.multiLinked) {
    return {
      numberFormat: documentPart.metadata.formatMetadata.numberFormat.format,
      typeNumber: documentPart.metadata.formatMetadata.numberType ?? TYPE_CUSTOM,
      percentageFormat: documentPart.metadata.formatMetadata.percentageFormat.format,
      typePercentage: documentPart.metadata.formatMetadata.percentageType ?? TYPE_CUSTOM,
      dateFormat: documentPart.metadata.formatMetadata.dateFormat.format,
      typeDate: documentPart.metadata.formatMetadata.dateType ?? TYPE_CUSTOM,
      locale: documentPart.metadata.formatMetadata.locale,
      id: documentPart.metadata.id,
      choice: objectAdvanced.choice,
      inheritFormat: true,
    };
  }
  // returns null if inherit true, but the document is not root and is multilinked (error notification)
  return null;
};

const validate = (obj: AdvancedDocumentMetadata, documentPart: DocumentPart) => {
  if (!obj) return false;

  const { numberFormat, percentageFormat } = obj;

  if (numberFormat == "") return false;
  if (percentageFormat == "") return false;
  return !(obj.inheritFormat && documentPart.multiLinked && !documentPart.root);
};

export const CoObjectAdvancedAmountPanel: React.FunctionComponent<CoObjectAdvancedAmountProps> = (props) => {
  const { objectAdvanced, updateCoObjectAdvancedAmount, isEdit, isComponentInstance, documentPartId, isLocked } = props;
  const [state, setState] = useState<{
    preview: string;
    editAmountCustomXML: DocumentMetadata;
    documentPart: DocumentPart;
  }>({
    preview: "",
    editAmountCustomXML: null,
    documentPart: null,
  });

  function validateFields(obj: AdvancedDocumentMetadata, dp: DocumentPart): ObjectPanelTabs {
    return { key: props.tab.key, name: props.tab.name, isValid: validate(obj, dp) };
  }

  const { editAmountCustomXML, documentPart } = state;

  const updateFormatPreview = (newObjectAdvanced) => {
    if (!newObjectAdvanced) {
      setState((s) => ({ ...s, preview: "" }));
      return;
    }
    const objectPreview: FormatPreview = {
      value: DEFAULT_VALUE,
      format:
        newObjectAdvanced.choice === FormatTypeEnum.NUMBER
          ? newObjectAdvanced.numberFormat
          : newObjectAdvanced.percentageFormat,
      typeFormat: FormatTypeEnum.NUMBER,
      typeFormatChoice:
        newObjectAdvanced.choice === FormatTypeEnum.NUMBER
          ? newObjectAdvanced.typeNumber
          : newObjectAdvanced.typePercentage,
      locale: newObjectAdvanced.locale,
    };
    formatPreview(objectPreview).then((value) => setState((s) => ({ ...s, preview: value })));
  };

  useEffect(() => {
    getInitialDocumentSettings();
  }, []);

  async function getInitialDocumentSettings() {
    const newCustomXML = await getDocumentAdvancedCustomXml();
    const newDocumentPart = await fetchDocumentPartDetails(documentPartId);
    setState((s) => ({ ...s, editAmountCustomXML: newCustomXML, documentPart: newDocumentPart }));

    const advancedDocument = getUpdatedAdvancedDocument(
      isEdit || isComponentInstance
        ? objectAdvanced
        : {
            ...objectAdvanced,
            choice: FormatTypeEnum.NUMBER,
            numberFormat: getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
            typeNumber: TYPE_DEFAULT,
            inheritFormat: false,
          },
      newDocumentPart,
      newCustomXML
    );
    if (!advancedDocument)
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.createnewobject.inheritformat.error"),
        type: "error",
      });

    updateCoObjectAdvancedAmount(
      advancedDocument ?? objectAdvanced,
      validateFields(advancedDocument, newDocumentPart),
      !isEdit
    );
    updateFormatPreview(advancedDocument ?? objectAdvanced);
  }

  const options: IChoiceGroupOption[] = [
    { key: FormatTypeEnum.NUMBER, text: formatMessage("cof.createnewobject.asnumber") },
    { key: FormatTypeEnum.PERCENTAGE, text: formatMessage("cof.createnewobject.aspercentage") },
  ];

  const onChangeFormatAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    const advancedDocument = getUpdatedAdvancedDocument(objectAdvanced, documentPart, editAmountCustomXML);
    if (advancedDocument) {
      advancedDocument.choice = option.key;
      updateFormatPreview(advancedDocument);
      updateCoObjectAdvancedAmount(advancedDocument, validateFields(advancedDocument, documentPart), true);
    }
    //maybe set error notification in the else case(?)
  };

  const onChangeToggle = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    const advancedDocument = getUpdatedAdvancedDocument(
      { ...objectAdvanced, inheritFormat: checked },
      documentPart,
      editAmountCustomXML
    );

    updateFormatPreview(advancedDocument);
    updateCoObjectAdvancedAmount(advancedDocument, validateFields(advancedDocument, documentPart), true);
  };

  const getFormattingStringInputCaptionId = () => {
    switch (objectAdvanced?.choice) {
      case FormatTypeEnum.NUMBER:
        return formatMessage("cof.base.number");
      case FormatTypeEnum.PERCENTAGE:
        return formatMessage("cof.base.percentage");
    }
  };

  const optionsChoice = [
    { key: TYPE_DEFAULT, text: formatMessage("cof.base.default") },
    { key: TYPE_CUSTOM, text: formatMessage("cof.base.custom") },
  ];

  const onChangeFormattingString = (value, typeFormatChoice) => {
    const advancedDocument = getUpdatedAdvancedDocument(objectAdvanced, documentPart, editAmountCustomXML);

    if (advancedDocument.choice === FormatTypeEnum.NUMBER) {
      advancedDocument.numberFormat = value;
      advancedDocument.typeNumber = typeFormatChoice;
    } else {
      advancedDocument.percentageFormat = value;
      advancedDocument.typePercentage = typeFormatChoice;
    }

    updateFormatPreview(advancedDocument);
    updateCoObjectAdvancedAmount(advancedDocument, validateFields(advancedDocument, documentPart), true);
  };

  const infoId = "AdvancedDocumentPanelInfoId";

  const [stateInfo, setStateInfo] = useState<InfoState>({
    teachingBubbleVisible: false,
  });

  return (
    <>
      {stateInfo.teachingBubbleVisible && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setStateInfo({ ...stateInfo, teachingBubbleVisible: false })}
          headline={formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.title")}
        >
          <text>{formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1.15rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.base.formatoptions")}
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setStateInfo({ ...stateInfo, teachingBubbleVisible: !stateInfo.teachingBubbleVisible })}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              disabled={isComponentInstance || isLocked}
              selectedKey={objectAdvanced?.choice}
              defaultSelectedKey={FormatTypeEnum.NUMBER}
              styles={{
                flexContainer: { display: "flex", justifyContent: "left", columnGap: "0.5rem", flexWrap: "wrap" },
              }}
              options={options}
              onChange={onChangeFormatAs}
              label={formatMessage("cof.createnewobject.formatstarting")}
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
            <Label style={{ float: "left" }}>{getFormattingStringInputCaptionId()}</Label>
          </div>
          <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
            <Toggle
              label={formatMessage("cof.createnewobject.inheritformat")}
              inlineLabel
              checked={objectAdvanced?.inheritFormat}
              onChange={onChangeToggle}
              disabled={
                isComponentInstance ||
                isLocked ||
                (!objectAdvanced?.inheritFormat && (!documentPart || (documentPart.multiLinked && !documentPart.root)))
              }
              onText=" "
              offText=" "
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
              selectedKey={
                objectAdvanced?.choice === FormatTypeEnum.NUMBER
                  ? objectAdvanced?.typeNumber ?? TYPE_DEFAULT
                  : objectAdvanced?.typePercentage ?? TYPE_DEFAULT
              }
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              onChange={(_, choice) => {
                onChangeFormattingString(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(objectAdvanced?.choice)
                    : getDefaultBuiltInFormattingString(objectAdvanced?.choice),
                  choice.key
                );
              }}
            />
            {(objectAdvanced?.choice === FormatTypeEnum.NUMBER
              ? objectAdvanced?.typeNumber === TYPE_DEFAULT
              : objectAdvanced?.typePercentage === TYPE_DEFAULT) && (
              <Dropdown
                disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
                data-testid={"number-default"}
                options={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER ? builtInNumberFormat : builtInPercentageFormat
                }
                onChange={(_event, option) => {
                  onChangeFormattingString(option.key, objectAdvanced?.typeNumber);
                }}
                selectedKey={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER
                    ? builtInNumberFormat.find((f) => f.key == objectAdvanced?.numberFormat)?.key
                    : builtInPercentageFormat.find((f) => f.key == objectAdvanced?.percentageFormat)?.key
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {(objectAdvanced?.choice === FormatTypeEnum.NUMBER
              ? objectAdvanced?.typeNumber === TYPE_CUSTOM
              : objectAdvanced?.typePercentage === TYPE_CUSTOM) && (
              <TextField
                disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
                value={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER
                    ? objectAdvanced?.numberFormat ?? getDefaultCustomFormattingString(objectAdvanced?.choice)
                    : objectAdvanced?.percentageFormat ?? getDefaultCustomFormattingString(objectAdvanced?.choice)
                }
                onChange={(e) =>
                  onChangeFormattingString(
                    (e.target as HTMLInputElement)?.value ?? "",
                    objectAdvanced?.choice === FormatTypeEnum.NUMBER
                      ? objectAdvanced?.typeNumber
                      : objectAdvanced?.typePercentage
                  )
                }
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.preview}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
